import {
  FUMBLE_INPUTS,
  PASS_RESULT_INPUTS,
  REF_EVENT_INPUTS,
  REF_EVENT_INPUTS_NO_REVIEW_CHALLENGE,
  TEventTypes,
  TPlayDesigns,
  TPlayResultTypes,
  TURNOVER_BTN,
  YARDAGE_SELECTOR_BUTTONS,
} from '@/constants';
import {
  ITwoRowGenericSelectorInp,
  TMachineEvents,
  TMetaFuncInput,
  TPassPlays,
  TPlays,
  TRefEventsActions,
  TRegularPlayEvents,
} from '@/types';
import { refEventValue } from '@/utilities';
import { isTurnoverResult, pushResultType, sendPenaltyEvent } from '@/workflow/utils';
import { cloneDeep, last } from 'lodash';

export const onPlayDesignSelected = ({
  send,
  payload,
  generalState,
  playFlowState,
  context,
}: TMetaFuncInput) => {
  switch (payload.value) {
    case TPlays.PASS: {
      playFlowState.pushEvent(
        {
          eventTypeId: TEventTypes.PLAY_DESIGN,
          value: TPlayDesigns.PASS,
        },
        generalState.currentGame.gameId
      );

      const currentPlay = {
        ...context.currentPlay,
        playDesignId: TPlayDesigns.PASS,
      };

      send(TRegularPlayEvents.ON_PASS_SELECTED, {
        nextStateInput: PASS_RESULT_INPUTS,
        ctxUpdate: { currentPlay },
      });
      break;
    }
    case TPlays.RUSH: {
      playFlowState.pushEvent(
        {
          eventTypeId: TEventTypes.PLAY_DESIGN,
          value: TPlayDesigns.RUSH,
        },
        generalState.currentGame.gameId
      );

      const currentPlay = {
        ...context.currentPlay,
        playDesignId: TPlayDesigns.RUSH,
        resultType: [TPlayResultTypes.RUSH_ATTENPT],
      };

      send(TRegularPlayEvents.ON_RESULT_SELECTED, {
        nextStateInput: {
          buttons: YARDAGE_SELECTOR_BUTTONS(payload.toggles[TPlays.PENALTY_ALERT]),
          sliderLabels: {
            left: generalState.teams[playFlowState.teams.left].abbreviation,
            right: generalState.teams[playFlowState.teams.right].abbreviation,
          },
          backTo: TRegularPlayEvents.BACK_TO_PLAY_DESIGN,
        },
        ctxUpdate: { currentPlay },
      });
      break;
    }
    case TPlays.FUMBLE:
      playFlowState.pushEvent(
        {
          eventTypeId: TEventTypes.POTENTIAL_TOV,
          value: true,
        },
        generalState.currentGame.gameId
      );

      send(TRegularPlayEvents.FUMBLE, {
        nextStateInput: FUMBLE_INPUTS(),
        ctxUpdate: {
          currentPlay: {
            ...context.currentPlay,
            playDesignId: TPlayDesigns.RUSH,
          },
        },
      });
      break;
    case TPlays.REF:
      playFlowState.pushEvent(
        {
          eventTypeId: TEventTypes.REF_EVENT,
          value: refEventValue(playFlowState.driveNumber, playFlowState.drivePlayNumber),
        },
        generalState.currentGame.gameId
      );

      send(TRegularPlayEvents.REF_EVENT, {
        nextStateInput: {
          ...REF_EVENT_INPUTS,
          backTo: TRefEventsActions.BACK_FROM_REF,
        } as ITwoRowGenericSelectorInp,
        ctxUpdate: {
          currentPlay: {
            ...context.currentPlay,
            playDesignId: TPlayDesigns.NO_PLAY,
          },
        },
      });
      break;
    default:
  }
};

export const onPassResultSelected = ({
  send,
  payload,
  generalState,
  playFlowState,
  context,
}: TMetaFuncInput) => {
  if (payload.toggles[TPlays.PENALTY_ALERT] && payload.value === TPassPlays.INCOMPLETE) {
    return sendPenaltyEvent(
      playFlowState,
      generalState,
      send,
      {
        ...context.currentPlay,
        playDesignId: TPlayDesigns.PASS,
      },
      TRegularPlayEvents.ON_PENALTY,
      TRefEventsActions.BACK_TO_PASS_RESULT
    );
  }

  const yardageNextInput = {
    buttons: YARDAGE_SELECTOR_BUTTONS(payload.toggles[TPlays.PENALTY_ALERT]),
    sliderLabels: {
      left: generalState.teams[playFlowState.teams.left].abbreviation,
      right: generalState.teams[playFlowState.teams.right].abbreviation,
    },
    backTo: TRegularPlayEvents.BACK_TO_PASS_RESULT,
  };

  switch (payload.value) {
    case TPassPlays.QB_SCRAMBLE: {
      const currentPlay = {
        ...context.currentPlay,
        resultType: pushResultType(context, TPlayResultTypes.RUSH_ATTENPT),
        qbScramble: true,
        options: {
          ...context.currentPlay?.options,
          isPenaltyToggled: payload.toggles[TPlays.PENALTY_ALERT],
        },
      };

      return send(TRegularPlayEvents.ON_RESULT_SELECTED, {
        nextStateInput: yardageNextInput,
        ctxUpdate: {
          currentPlay,
        },
      });
    }
    case TPassPlays.INCOMPLETE: {
      const nextStateInput = {
        backTo: TRegularPlayEvents.BACK_TO_PASS_RESULT,
      } as any;
      const resultTypes = cloneDeep(context.currentPlay?.resultType || []);
      const isTurnover = isTurnoverResult(last(resultTypes));

      const isTurnoverOnDowns = playFlowState.presentStateBasedOnCurrentMode.down + 1 > 4;

      resultTypes.push(TPlayResultTypes.PASS_INCOMP);

      if (isTurnoverOnDowns || isTurnover) {
        nextStateInput.toggleButton = cloneDeep(TURNOVER_BTN);
      }

      playFlowState.pushEvent(
        {
          eventTypeId: TEventTypes.INCOMPLETE_PASS,
          value: true,
        },
        generalState.currentGame.gameId
      );

      const currentPlay = {
        ...context.currentPlay,
        resultType: resultTypes,
        qbScramble: false,
        postPlayYardLine: playFlowState.presentStateBasedOnCurrentMode.yardLine.number,
        postPlaySide: playFlowState.presentStateBasedOnCurrentMode.yardLine.side,
      };

      return send(TMachineEvents.PLAY_COMPLETED, {
        nextStateInput: {
          backTo: TRegularPlayEvents.BACK_TO_PASS_RESULT,
        },
        ctxUpdate: {
          currentPlay,
        },
      });
    }
    case TPassPlays.COMPLETE: {
      const currentPlay = {
        ...context.currentPlay,
        resultType: pushResultType(context, TPlayResultTypes.PASS_COMP),
        qbScramble: false,
        options: {
          ...context.currentPlay?.options,
          isPenaltyToggled: payload.toggles[TPlays.PENALTY_ALERT],
        },
      };

      return send(TRegularPlayEvents.ON_RESULT_SELECTED, {
        nextStateInput: yardageNextInput,
        ctxUpdate: {
          currentPlay,
        },
      });
    }
    case TPassPlays.INTERCEPTION:
      playFlowState.pushEvent(
        {
          eventTypeId: TEventTypes.POTENTIAL_TOV,
          value: true,
        },
        generalState.currentGame.gameId
      );

      return send(TRegularPlayEvents.ON_RESULT_SELECTED, {
        nextStateInput: {
          buttons: YARDAGE_SELECTOR_BUTTONS(),
          sliderLabels: {
            left: generalState.teams[playFlowState.teams.left].abbreviation,
            right: generalState.teams[playFlowState.teams.right].abbreviation,
          },
          backTo: TRegularPlayEvents.BACK_TO_PASS_RESULT,
        },
        ctxUpdate: {
          currentPlay: {
            ...context.currentPlay,
            resultType: pushResultType(context, TPlayResultTypes.INTERCEPTION),
            options: {
              isTov: true,
            },
          },
        },
      });
    case TPlays.REF:
      playFlowState.pushEvent(
        {
          eventTypeId: TEventTypes.REF_EVENT,
          value: refEventValue(playFlowState.driveNumber, playFlowState.drivePlayNumber),
        },
        generalState.currentGame.gameId
      );

      return send(TRegularPlayEvents.REF_EVENT, {
        nextStateInput: {
          ...REF_EVENT_INPUTS_NO_REVIEW_CHALLENGE,
          backTo: TRefEventsActions.BACK_TO_PASS_RESULT,
        } as ITwoRowGenericSelectorInp,
        currentPlay: {
          ...context.currentPlay,
          qbScramble: false,
          playDesignId: TPlayDesigns.NO_PLAY,
          options: {
            ...context.currentPlay?.options,
            isPenaltyToggled: payload.toggles[TPlays.PENALTY_ALERT],
          },
        },
      });
    default:
      return send(TRegularPlayEvents.BACK);
  }
};
